import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "./../../../hooks/useScollToTop";
import {
  getTechniquesJudo,
  getJudoTechniquesWithoutSelfDefense,
} from "../../../services/techniquesServices";
import HeroImage from "../../../components/Hero/HeroImage";
import {
  FaCheckCircle,
  FaDumbbell,
  FaPlay,
  FaRedo,
  FaShieldAlt,
  FaTimesCircle,
  FaTrophy,
} from "react-icons/fa";

const GRADE_OPTIONS = [
  "Toutes les ceintures",
  "Jaune",
  "Orange",
  "Verte",
  "Bleue",
  "Marron",
  "Noire",
];

const shuffleArray = (arr = []) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const getImageUrl = (technique) =>
  technique?.image1_url || "/img/default_image.jpg";

const buildQuestionSet = (allTechniques = [], grade = "Toutes les ceintures") => {
  const filteredTechniques =
    grade === "Toutes les ceintures"
      ? allTechniques
      : allTechniques.filter((tech) => tech.ceinture === grade);

  const selectedBase = shuffleArray(filteredTechniques).slice(
    0,
    Math.min(10, filteredTechniques.length)
  );

  return selectedBase.map((technique) => {
    const sameFamilyPool = filteredTechniques.filter(
      (t) => t.id !== technique.id && t.famille === technique.famille
    );

    const fallbackPool = filteredTechniques.filter((t) => t.id !== technique.id);
    const correctAnswer = technique.nom;

    const distractors = [];
    const usedNames = new Set([normalize(correctAnswer)]);

    const tryAddDistractors = (pool) => {
      shuffleArray(pool).forEach((item) => {
        const name = item?.nom;
        const key = normalize(name);
        if (!name || usedNames.has(key) || distractors.length >= 3) return;
        usedNames.add(key);
        distractors.push(name);
      });
    };

    tryAddDistractors(sameFamilyPool);
    tryAddDistractors(fallbackPool);

    const options = shuffleArray([correctAnswer, ...distractors]).slice(0, 4);

    return {
      id: technique.id,
      image: getImageUrl(technique),
      correctAnswer,
      options,
      famille: technique.famille,
      ceinture: technique.ceinture,
    };
  });
};

function StatChip({ label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
          {label}
        </div>
        <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
          {value}
        </div>
      </div>
    </div>
  );
}

function AnswerButton({
  option,
  selectedAnswer,
  correctAnswer,
  hasAnswered,
  onClick,
}) {
  const isSelected = selectedAnswer === option;
  const isCorrect = correctAnswer === option;

  let className =
    "w-full rounded-2xl border px-4 py-4 text-left text-sm font-semibold transition ";

  if (!hasAnswered) {
    className +=
      "border-slate-200 bg-white text-slate-800 hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-indigo-500/40 dark:hover:bg-slate-800";
  } else if (isCorrect) {
    className +=
      "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300";
  } else if (isSelected) {
    className +=
      "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300";
  } else {
    className +=
      "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400";
  }

  return (
    <button
      type="button"
      onClick={() => onClick(option)}
      disabled={hasAnswered}
      className={className}
    >
      {option}
    </button>
  );
}

const TechniquesQuizz = () => {
  useScrollToTop();

  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);

  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answerHistory, setAnswerHistory] = useState([]);

  const [grade, setGrade] = useState("Toutes les ceintures");
  const [includeSelfDefense, setIncludeSelfDefense] = useState(false);

  useEffect(() => {
    const fetchTechniques = async () => {
      try {
        setLoading(true);
        const fetchedTechniques = includeSelfDefense
          ? await getTechniquesJudo()
          : await getJudoTechniquesWithoutSelfDefense();

        setTechniques(Array.isArray(fetchedTechniques) ? fetchedTechniques : []);
      } catch (error) {
        console.error("Erreur chargement techniques :", error);
        setTechniques([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTechniques();
  }, [includeSelfDefense]);

  const availableTechniques = useMemo(() => {
    if (grade === "Toutes les ceintures") return techniques;
    return techniques.filter((tech) => tech.ceinture === grade);
  }, [techniques, grade]);

  const generateQuiz = () => {
    const generated = buildQuestionSet(techniques, grade);
    setQuestions(generated);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setHasAnswered(false);
    setScore(0);
    setShowResults(false);
    setAnswerHistory([]);
  };

  useEffect(() => {
    if (techniques.length > 0) {
      generateQuiz();
    } else {
      setQuestions([]);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setHasAnswered(false);
      setScore(0);
      setShowResults(false);
      setAnswerHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techniques, grade]);

  const current = questions[currentQuestion] || null;
  const totalQuestions = questions.length;
  const progressPercentage = totalQuestions
    ? ((currentQuestion + 1) / totalQuestions) * 100
    : 0;

  const handleAnswer = (answer) => {
    if (!current || hasAnswered) return;

    const isCorrect = answer === current.correctAnswer;

    setSelectedAnswer(answer);
    setHasAnswered(true);

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }

    setAnswerHistory((prev) => [
      ...prev,
      {
        ...current,
        selectedAnswer: answer,
        isCorrect,
      },
    ]);
  };

  const handleNext = () => {
    if (!hasAnswered) return;

    if (currentQuestion === totalQuestions - 1) {
      setShowResults(true);
      return;
    }

    setCurrentQuestion((prev) => prev + 1);
    setSelectedAnswer(null);
    setHasAnswered(false);
  };

  const handleRestart = () => {
    generateQuiz();
  };

  return (
    <div className="w-full pt-[96px] font-satoshi">
      <HeroImage
        title="Quiz Techniques"
        subtitle="Testez vos connaissances sur les techniques de judo à partir d’images et de propositions ciblées."
        imageUrl="/img/cover/cover_techniques.jpg"
      />

      <div className="mx-auto max-w-[1280px] px-4 pb-10 pt-6">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Entraînement
                </p>
                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Reconnaissance des techniques
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                  Choisissez votre niveau, ajoutez ou non la self-défense, puis
                  lancez une série de 10 questions maximum.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[720px]">
                <StatChip
                  label="Techniques"
                  value={techniques.length}
                  accent="indigo"
                />
                <StatChip
                  label="Disponibles"
                  value={availableTechniques.length}
                  accent="emerald"
                />
                <StatChip
                  label="Questions"
                  value={questions.length}
                  accent="amber"
                />
              </div>
            </div>
          </div>
        </div>

        {!showResults && (
          <div className="mt-5 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),auto]">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="grade"
                    className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-100"
                  >
                    Niveau / ceinture
                  </label>
                  <select
                    id="grade"
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                  >
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => setIncludeSelfDefense((prev) => !prev)}
                    className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition ${
                      includeSelfDefense
                        ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300"
                        : "border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    }`}
                  >
                    <FaShieldAlt />
                    {includeSelfDefense
                      ? "Self-Défense incluse"
                      : "Ajouter Self-Défense"}
                  </button>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={generateQuiz}
                  disabled={loading || availableTechniques.length === 0}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-60"
                >
                  <FaPlay />
                  Générer un nouveau quiz
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-5 rounded-[28px] border border-slate-200 bg-white px-6 py-14 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            Chargement des techniques…
          </div>
        ) : availableTechniques.length === 0 ? (
          <div className="mt-5 rounded-[28px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <FaDumbbell className="text-2xl" />
            </div>

            <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
              Aucune technique disponible
            </h3>

            <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
              Aucun contenu ne correspond au filtre sélectionné.
            </p>
          </div>
        ) : showResults ? (
          <div className="mt-5 space-y-5">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                    Résultats
                  </p>
                  <h2 className="mt-1 text-3xl font-black text-slate-900 dark:text-white">
                    {score} / {totalQuestions}
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {Math.round((score / Math.max(totalQuestions, 1)) * 100)}% de bonnes réponses
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleRestart}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  <FaRedo />
                  Recommencer
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {answerHistory.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className={`overflow-hidden rounded-[24px] border shadow-sm ${
                    item.isCorrect
                      ? "border-emerald-200 bg-white dark:border-emerald-500/20 dark:bg-slate-900"
                      : "border-rose-200 bg-white dark:border-rose-500/20 dark:bg-slate-900"
                  }`}
                >
                  <div
                    className={`h-1.5 w-full ${
                      item.isCorrect ? "bg-emerald-500" : "bg-rose-500"
                    }`}
                  />

                  <div className="p-4">
                    <img
                      src={item.image}
                      alt={item.correctAnswer}
                      className="h-56 w-full rounded-2xl object-cover bg-slate-100 dark:bg-slate-800"
                    />

                    <div className="mt-4">
                      <div className="flex items-center gap-2">
                        {item.isCorrect ? (
                          <FaCheckCircle className="text-emerald-600 dark:text-emerald-300" />
                        ) : (
                          <FaTimesCircle className="text-rose-600 dark:text-rose-300" />
                        )}

                        <span className="text-sm font-semibold text-slate-900 dark:text-white">
                          {item.isCorrect ? "Bonne réponse" : "Réponse incorrecte"}
                        </span>
                      </div>

                      <div className="mt-3 text-sm leading-6">
                        {!item.isCorrect ? (
                          <p className="text-slate-600 dark:text-slate-300">
                            <span className="font-semibold text-rose-600 dark:text-rose-300">
                              Votre réponse :
                            </span>{" "}
                            {item.selectedAnswer}
                          </p>
                        ) : null}

                        <p className="mt-1 text-slate-700 dark:text-slate-200">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                            Bonne réponse :
                          </span>{" "}
                          {item.correctAnswer}
                        </p>

                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Famille : {item.famille || "—"} • Grade : {item.ceinture || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : current ? (
          <div className="mt-5 grid gap-5 xl:grid-cols-[420px,minmax(0,1fr)]">
            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                    Progression
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                    Question {currentQuestion + 1} / {totalQuestions}
                  </h2>
                </div>

                <div className="rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Score : {score}
                </div>
              </div>

              <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F] transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Détails
                </p>

                <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  <p>
                    <span className="font-semibold">Famille :</span>{" "}
                    {current.famille || "—"}
                  </p>
                  <p>
                    <span className="font-semibold">Ceinture :</span>{" "}
                    {current.ceinture || "—"}
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-3">
                {current.options.map((option, index) => (
                  <AnswerButton
                    key={`${option}-${index}`}
                    option={option}
                    selectedAnswer={selectedAnswer}
                    correctAnswer={current.correctAnswer}
                    hasAnswered={hasAnswered}
                    onClick={handleAnswer}
                  />
                ))}
              </div>

              {hasAnswered ? (
                <div
                  className={`mt-5 rounded-2xl border p-4 ${
                    selectedAnswer === current.correctAnswer
                      ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10"
                      : "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {selectedAnswer === current.correctAnswer ? (
                      <FaCheckCircle className="mt-0.5 text-emerald-600 dark:text-emerald-300" />
                    ) : (
                      <FaTimesCircle className="mt-0.5 text-rose-600 dark:text-rose-300" />
                    )}

                    <div>
                      <p className="font-bold text-slate-900 dark:text-white">
                        {selectedAnswer === current.correctAnswer
                          ? "Bonne réponse"
                          : "Réponse incorrecte"}
                      </p>
                      {selectedAnswer !== current.correctAnswer ? (
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                          La bonne réponse était{" "}
                          <span className="font-semibold">
                            {current.correctAnswer}
                          </span>
                          .
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="mt-4 inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    {currentQuestion === totalQuestions - 1
                      ? "Voir mes résultats"
                      : "Question suivante"}
                  </button>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  Sélectionnez la bonne technique à partir de l’image affichée.
                </div>
              )}
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-800">
                <img
                  src={current.image}
                  alt="Technique"
                  className="h-[520px] w-full object-contain"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TechniquesQuizz;